import { useEffect, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import List from '@mui/material/List';
import Divider from '@mui/material/Divider';
import Link from '@mui/material/Link';
import Icon from '@mui/material/Icon';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import { useAuth } from 'context/auth-context';
import { authStorage } from '@functions/auth-session';
import { accountButtonStyles, accountMenuStyles } from './sidenav-account.styles';

import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import SidenavCollapse from 'examples/Sidenav/SidenavCollapse';
import { usePending } from '@layouts/pages/CRM/agenda/hooks/use-pending.hook';
import { activeMenuKey } from '@functions/allowed-routes';
import SidenavList from 'examples/Sidenav/SidenavList';
import SidenavItem from 'examples/Sidenav/SidenavItem';
import SidenavRoot from 'examples/Sidenav/SidenavRoot';
import miniLogo from '../../assets/images/brand/logoMGCodeMiniSideNav.png';
import miniLogoDark from '../../assets/images/brand/logoMGCodeMiniSideNavDarkMode.png';
import {
    setMiniSidenav,
    setOpenConfigurator,
    setTransparentSidenav,
    useMaterialUIController,
} from 'context';
import { useTranslation } from 'react-i18next';
import { AppRoute } from 'types/app-route';

//aqui não é bom tirar
interface Props {
    color?: 'primary' | 'secondary' | 'info' | 'success' | 'warning' | 'error' | 'light' | 'dark';
    brand?: string;
    brandName: string;
    routes: AppRoute[];

}


function charge(): void {
    window.history.pushState(null, '', '/home');
    window.location.reload();
}

function Sidenav({ color, brand, brandName, routes, ...rest }: Props): JSX.Element {
    const [openCollapse, setOpenCollapse] = useState<boolean | string>(false);
    const [openNestedCollapse, setOpenNestedCollapse] = useState<boolean | string>(false);
    const [controller, dispatch] = useMaterialUIController();
    const { miniSidenav, transparentSidenav, whiteSidenav, darkMode, openConfigurator } = controller;
    const { signOut } = useAuth();
    const navigate = useNavigate();
    const [accountAnchor, setAccountAnchor] = useState<HTMLElement | null>(null);
    const email = authStorage.read()?.email;
    const location = useLocation();
    const { pathname } = location;
    const pending = usePending(pathname);
    const { t } = useTranslation();
    const collapseName = pathname.split('/').slice(1)[0];
    const items = pathname.split('/').slice(1);
    const itemParentName = items[1];
    const itemName = items[items.length - 1];
    const activeKey = activeMenuKey(routes, pathname);

    let textColor:
        | 'primary'
        | 'secondary'
        | 'info'
        | 'success'
        | 'warning'
        | 'error'
        | 'dark'
        | 'white'
        | 'inherit'
        | 'text'
        | 'light' = 'white';

    if (transparentSidenav || (whiteSidenav && !darkMode)) {
        textColor = 'dark';
    } else if (whiteSidenav && darkMode) {
        textColor = 'inherit';
    }

    const closeSidenav = () => setMiniSidenav(dispatch, true);

    const openSettings = () => {
        setAccountAnchor(null);
        setOpenConfigurator(dispatch, !openConfigurator);
    };

    const logout = () => {
        setAccountAnchor(null);
        // Só a sessão: limpar tudo levaria junto tema e preferências de tela.
        signOut();
        // Pelo roteador, e não pelo history do navegador: trocar só o endereço deixava o roteador na tela antiga.
        navigate('/', { replace: true });
    };

    // Acompanha a URL: navegar por código (ex.: abrir o lead pelo Multicanal) também troca o grupo aberto.
    useEffect(() => {
        setOpenCollapse(collapseName);
        setOpenNestedCollapse(itemParentName);
    }, [collapseName, itemParentName]);

    useEffect(() => {
        // A function that sets the mini state of the sidenav.
        function handleMiniSidenav() {
            setMiniSidenav(dispatch, window.innerWidth < 1200);
            setTransparentSidenav(dispatch, window.innerWidth < 1200 ? false : transparentSidenav);
        }

        /**
        The event listener that's calling the handleMiniSidenav function when resizing the window.
         */
        window.addEventListener('resize', handleMiniSidenav);

        // Call the handleMiniSidenav function to set the state with the initial value.
        handleMiniSidenav();

        // Remove event listener on cleanup
        return () => window.removeEventListener('resize', handleMiniSidenav);
    }, [dispatch, location]);

    // Render all the nested collapse items from the routes.js
    const renderNestedCollapse = (collapse: AppRoute[]) => {
        const template = collapse.map(({ name, route, key, href }) =>
            href ? (
                <Link
                    key={key}
                    href={href}
                    target="_blank"
                    rel="noreferrer"
                    sx={{ textDecoration: 'none' }}
                >
                    <SidenavItem name={name} color={color} nested />
                </Link>
            ) : (
                <NavLink to={route} key={key} style={{ textDecoration: 'none' }}>
                    <SidenavItem name={name} active={route === pathname} color={color} nested />
                </NavLink>
            )
        );

        return template;
    };

    // Render the all the collpases from the routes.js
    const renderCollapse = (collapses: AppRoute[]) =>
        collapses.map(({ name, collapse, route, href, key, icon, show }) => {
            let returnValue;
            const iconName = icon;

            if (collapse) {
                returnValue = (
                    <SidenavItem
                        key={key}
                        icon={icon}
                        name={name}
                        color={color}
                        active={key === itemParentName ? 'isParent' : false}
                        open={openNestedCollapse === key}
                        onClick={({ currentTarget }) =>
                            openNestedCollapse === key &&
                                currentTarget.classList.contains('MuiListItem-root')
                                ? setOpenNestedCollapse(false)
                                : setOpenNestedCollapse(key)
                        }
                    >
                        {renderNestedCollapse(collapse)}
                    </SidenavItem>
                );
            } else if (show !== false) {
                returnValue = href ? (
                    <Link
                        href={href}
                        key={key}
                        target="_blank"
                        rel="noreferrer"
                        sx={{ textDecoration: 'none' }}
                    >
                        <SidenavItem
                            icon={icon}
                            color={color}
                            name={name}
                            active={key === itemName}
                        />
                    </Link>
                ) : (
                    <NavLink to={route} key={key} style={{ textDecoration: 'none' }}>
                        <SidenavItem
                            icon={iconName}
                            name={name}
                            color={color}
                            active={key === activeKey}
                            badge={key === 'agenda' ? pending.total : undefined}
                            badgeHint={key === 'agenda' ? t('agenda.seloAjuda') : undefined}
                        />
                    </NavLink>
                );
            }
            return <SidenavList key={key}>{returnValue}</SidenavList>;
        });

    const renderRoutes = routes.map(
        ({ type, name, icon, title, collapse, noCollapse, key, href, route }) => {
            let returnValue;

            if (type === 'collapse') {
                if (href) {
                    returnValue = (
                        <Link
                            href={href}
                            key={key}
                            target="_blank"
                            rel="noreferrer"
                            sx={{ textDecoration: 'none' }}
                        >
                            <SidenavCollapse
                                name={name}
                                icon={icon}
                                active={key === collapseName}
                                noCollapse={noCollapse}
                            />
                        </Link>
                    );
                } else if (noCollapse && route) {
                    returnValue = (
                        <NavLink to={route} key={key}>
                            <SidenavCollapse
                                name={name}
                                icon={icon}
                                noCollapse={noCollapse}
                                active={key === collapseName}
                            >
                                {collapse ? renderCollapse(collapse) : null}
                            </SidenavCollapse>
                        </NavLink>
                    );
                } else {
                    returnValue = (
                        <SidenavCollapse
                            key={key}
                            name={name}
                            icon={icon}
                            active={key === collapseName}
                            open={openCollapse === key}
                            onClick={() =>
                                openCollapse === key ? setOpenCollapse(false) : setOpenCollapse(key)
                            }
                        >
                            {collapse ? renderCollapse(collapse) : null}
                        </SidenavCollapse>
                    );
                }
            } else if (type === 'title') {
                returnValue = (
                    <MDTypography
                        key={key}
                        color={textColor}
                        display="block"
                        variant="caption"
                        fontWeight="bold"
                        textTransform="uppercase"
                        pl={3}
                        mt={2}
                        mb={1}
                        ml={1}
                    >
                        {title}
                    </MDTypography>
                );
            } else if (type === 'divider') {
                returnValue = (
                    <Divider
                        key={key}
                        light={
                            (!darkMode && !whiteSidenav && !transparentSidenav) ||
                            (darkMode && !transparentSidenav && whiteSidenav)
                        }
                    />
                );
            }

            return returnValue;
        }
    );

    return (
        <SidenavRoot
            {...rest}
            variant="permanent"
            ownerState={{ transparentSidenav, whiteSidenav, miniSidenav, darkMode }}
        >
            <MDBox
                sx={
                    miniSidenav
                        ? { paddingLeft: '0px!important', paddingRight: '0px!important' }
                        : {}
                }
                pt={3}
                pb={1}
                px={4}
                textAlign="center"
            >
                <MDBox
                    display={{ xs: 'block', xl: 'none' }}
                    position="absolute"
                    top={0}
                    right={0}
                    p={1.625}
                    onClick={closeSidenav}
                    sx={{ cursor: 'pointer' }}
                >
                    <MDTypography variant="h6" color="secondary">
                        <Icon sx={{ fontWeight: 'bold' }}>close</Icon>
                    </MDTypography>
                </MDBox>
                <MDBox
                    onClick={charge}
                >
                    {miniSidenav ? (
                        <Box component="img" src={darkMode ? miniLogoDark : miniLogo} alt="Brand" width="4rem" />
                    ) : (
                        <Box component="img" src={brand} alt="Brand" width="100%" />
                    )}
                </MDBox>
            </MDBox>

            <Divider
                light={
                    (!darkMode && !whiteSidenav && !transparentSidenav) ||
                    (darkMode && !transparentSidenav && whiteSidenav)
                }
            />
            <List>{renderRoutes}</List>

            {/* Conta no pé do menu: quem está logado, configurações visuais e sair. */}
            <Box sx={{ mt: 'auto', px: miniSidenav ? 1 : 2, pb: 2 }}>
                <Divider
                    light={
                        (!darkMode && !whiteSidenav && !transparentSidenav) ||
                        (darkMode && !transparentSidenav && whiteSidenav)
                    }
                />
                <ButtonBase
                    id="sidenav-account-button"
                    onClick={(event) => setAccountAnchor(event.currentTarget)}
                    aria-haspopup="menu"
                    aria-expanded={Boolean(accountAnchor)}
                    aria-controls={accountAnchor ? 'sidenav-account-menu' : undefined}
                    aria-label={email ?? ''}
                    title={email ?? ''}
                    sx={accountButtonStyles(transparentSidenav || whiteSidenav, Boolean(accountAnchor), miniSidenav)}
                >
                    <MDTypography color={textColor} sx={{ display: 'flex' }}>
                        <Icon>account_circle</Icon>
                    </MDTypography>
                    {!miniSidenav && (
                        <MDTypography variant="button" color={textColor} noWrap sx={{ minWidth: 0, flex: 1, textAlign: 'left' }}>
                            {email}
                        </MDTypography>
                    )}
                    <MDTypography color={textColor} sx={{ display: 'flex', flexShrink: 0 }}>
                        <Icon fontSize="small">{accountAnchor ? 'expand_less' : 'expand_more'}</Icon>
                    </MDTypography>
                </ButtonBase>
                <Menu
                    id="sidenav-account-menu"
                    anchorEl={accountAnchor}
                    open={Boolean(accountAnchor)}
                    onClose={() => setAccountAnchor(null)}
                    anchorOrigin={{ vertical: 'top', horizontal: miniSidenav ? 'left' : 'right' }}
                    transformOrigin={{ vertical: 'bottom', horizontal: miniSidenav ? 'left' : 'right' }}
                    disableAutoFocusItem={false}
                    slotProps={{
                        paper: { sx: accountMenuStyles(accountAnchor?.offsetWidth, miniSidenav, darkMode) },
                        list: { 'aria-labelledby': 'sidenav-account-button', sx: { py: 0 } },
                    }}
                >
                    <MenuItem onClick={openSettings}>
                        <ListItemIcon>
                            <Icon fontSize="small">settings</Icon>
                        </ListItemIcon>
                        {t('navbarScreen.configuracoesVisuais')}
                    </MenuItem>
                    <MenuItem onClick={logout}>
                        <ListItemIcon>
                            <Icon fontSize="small" color="error">logout</Icon>
                        </ListItemIcon>
                        {t('navbarScreen.sairDoSistema')}
                    </MenuItem>
                </Menu>
            </Box>
        </SidenavRoot>
    );
}

// Declaring default props for Sidenav
Sidenav.defaultProps = {
    color: 'info',
    brand: '',
};

export default Sidenav;
